import * as numeral from 'numeral'
import { Currency, MoneyDto } from '../config/i18nConfig'
import { Duration } from 'luxon'

// eslint-disable-next-line @typescript-eslint/no-require-imports
require('numeral/locales/nl-nl')
numeral.locale('en')

export function formatNumber(value: number | string, format: string = '0,0.00', to: string = 'nl-nl'): string {
    const num = numeral(value)
    numeral.locale(to)
    const newValue: string = num.format(format)
    // Reset locale to default.
    numeral.locale('en')
    return newValue
}

export function formatCurrencyDecimals(
    amount: string | number,
    currency: Currency,
    from: string = 'en',
    to: string = 'en',
    thousands: boolean = true,
): string {
    return formatDecimals(amount, currency.decimals, from, to, thousands)
}

export function formatInlineCurrency(value: string | number, currency: Currency): string {
    return `${currency.symbol} ${formatCurrencyDecimals(value, currency, 'en', 'nl-nl')}`
}

export function formatDecimals(amount: string | number, decimals: number, from: string = 'en', to: string = 'en', thousands: boolean = true): string {
    return convertNumber(amount, from, to, getDecimalFormat(decimals, thousands))
}

export function convertNumber(value: string | number, from: string, to: string, format: string = '0,0.00'): string {
    numeral.locale(from)
    return formatNumber(value, format, to)
}

export function parseNumber(value: string | number, from: string = 'nl-nl'): number {
    numeral.locale(from)
    const num = numeral(value)
    // Reset locale to default.
    numeral.locale('en')
    return num.value()
}

export function getDecimalFormat(decimals: number = 2, thousands: boolean = true): string {
    return (thousands ? '-0,0.' : '-0.') + '0'.repeat(decimals)
}

export function parseTime(time: string) {
    if (!time || time.indexOf(':') < 0) {
        return {
            hours: null,
            minutes: null,
        }
    }
    return {
        hours: parseInt(time.substring(0, time.indexOf(':')), 10),
        minutes: parseInt(time.substring(time.indexOf(':') + 1), 10),
    }
}

export function formatTimeInSeconds(timeInSeconds: number) {
    const seconds = timeInSeconds >= 60 ? timeInSeconds % 60 : timeInSeconds
    return `${formatTimeInMinutes((timeInSeconds - seconds) / 60)}:${formatNumber(seconds, '00')}`
}

export function formatTimeInMinutes(timeInMinutes: number) {
    const minutes = timeInMinutes >= 60 ? timeInMinutes % 60 : timeInMinutes
    const hours = Math.floor((timeInMinutes - minutes) / 60)
    return `${hours}:${formatNumber(minutes, '00')}`
}

export function parseTimeInHours(time: number) {
    const timeInMinutes = time * 60
    const minutes = timeInMinutes >= 60 ? timeInMinutes % 60 : timeInMinutes
    const hours = Math.floor((timeInMinutes - minutes) / 60)
    return `${hours}:${formatNumber(minutes, '00')}`
}

export function parseTimeInMinutes(time: string): number {
    const { hours, minutes } = parseTime(time)
    return hours * 60 + minutes
}

export function multiplyTime(time: string, multiplier: number = 1): number {
    const minutes: number = parseTimeInMinutes(time)
    return (minutes / 60) * multiplier
}

export function multiplyTimeNumber(
    time: string = '0:00',
    number: string = '0',
    format: string = '0,0.00',
    from: string = 'nl-nl',
    to: string = 'nl-nl',
): string {
    return formatNumber(multiplyTime(time, parseNumber(number, from)), format, to)
}

// Do not use this method in new code, it contains a hard-coded assumption that the currency always has 2 decimals.
export function multiplyRate(multiplier: string | number = '0.00', rate: MoneyDto): MoneyDto {
    return {
        amount: formatNumber(parseNumber(multiplier, 'en') * parseNumber(rate.amount, 'en'), '0.00', 'en'),
        currency: rate.currency,
    }
}

// Do not use this method in new code, it contains a hard-coded assumption that the currency always has 2 decimals.
export function multiplyTimeRate(time: string = '0:00', rate: MoneyDto): MoneyDto {
    return {
        amount: formatNumber(multiplyTime(time, parseNumber(rate.amount, 'en')), '0.00', 'en'),
        currency: rate.currency,
    }
}

export function addNumber(a: string | number, b: string | number, decimals: number = 2): string {
    return a && b ? formatDecimals(numeral(a).add(numeral(b).value()).value(), decimals, 'en', 'en') : ''
}

export function subtractNumber(a: string | number, b: string | number, format: string = '0,0.00', to: string = 'en'): string {
    return a && b ? formatNumber(numeral(a).subtract(numeral(b).value()).value(), format, to) : ''
}

export function doubleDigits(number: string | number): string {
    return parseInt(number.toString(), 10) < 10 ? '0' + number.toString() : number.toString()
}

export function parseMs(ms: number) {
    return {
        hours: Math.floor(ms / 1000 / 3600),
        minutes: Math.floor(((ms / 1000) % 3600) / 60),
        seconds: Math.floor(((ms / 1000) % 3600) % 60),
    }
}

export function reduceDuration(items: { duration: string }[]): Duration {
    return items
        .filter(item => item?.duration)
        .reduce((acc: Duration, item: { duration: string }) => acc.plus(Duration.fromObject(parseTime(item.duration))), Duration.fromMillis(0))
}

export function reduceFormattedDuration(items: { duration: string }[], format: string = 'h:mm'): string {
    return reduceDuration(items).toFormat(format)
}
